import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { constant } from 'src/app/constant/constant';
import { TokenStorageService } from 'src/app/services/utility/token-storage-service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { ReportsService } from './report.service';

@Injectable({
  providedIn: 'root'
})
export class ExcelDownloadStateService {
  private readonly PENDING_JOBS_KEY = 'pendingExcelJobIds';

  // Track SignalR connections
  private signalRConnections: { [jobId: string]: HubConnection } = {};

  private excelDownloadingSubject = new BehaviorSubject<boolean>(false);
  public excelDownloading$ = this.excelDownloadingSubject.asObservable();

  private excelDownloadedSubject = new BehaviorSubject<boolean>(false);
  public excelDownloaded$ = this.excelDownloadedSubject.asObservable();

  private pendingJobsSubject = new BehaviorSubject<string[]>([]);
  public pendingJobs$ = this.pendingJobsSubject.asObservable();

  private lastReadyJobId: string | null = null;
  public excelReady$ = new Subject<string>();
  public pdfReady$ = new Subject<string>();
  public excelJobFailed$ = new Subject<{ jobId: string; error: string }>();

  constructor(
    private tokenStorage: TokenStorageService,
    private utilityService: UtilityService,
    private reportService: ReportsService
  ) {
    this.updatePendingJobsFromStorage();
  }


  addPendingJob(jobId: string): void {
    const currentJobs = this.getPendingJobsFromStorage();
    if (!currentJobs.includes(jobId)) {
      currentJobs.push(jobId);
      this.savePendingJobsToStorage(currentJobs);
      this.updatePendingJobsFromStorage();
      this.updateDownloadingState();
    }
  }

  removePendingJob(jobId: string): void {
    let currentJobs = this.getPendingJobsFromStorage();
    currentJobs = currentJobs.filter(id => id !== jobId);
    this.savePendingJobsToStorage(currentJobs);
    this.updatePendingJobsFromStorage();
    this.updateDownloadingState();
  }

  clearAllPendingJobs(): void {
    localStorage.removeItem(this.PENDING_JOBS_KEY);
    this.pendingJobsSubject.next([]);
    this.setExcelDownloading(false);

    // Stop all SignalR connections
    Object.values(this.signalRConnections).forEach(conn => conn.stop());
    this.signalRConnections = {};
  }

  private getPendingJobsFromStorage(): string[] {
    try {
      const stored = localStorage.getItem(this.PENDING_JOBS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  private savePendingJobsToStorage(jobIds: string[]): void {
    localStorage.setItem(this.PENDING_JOBS_KEY, JSON.stringify(jobIds));
  }

  private updatePendingJobsFromStorage(): void {
    const jobs = this.getPendingJobsFromStorage();
    this.pendingJobsSubject.next(jobs);
  }

  private updateDownloadingState(): void {
    const pendingJobs = this.getPendingJobsFromStorage();
    this.setExcelDownloading(pendingJobs.length > 0);
  }

  setExcelDownloading(isDownloading: boolean) {
    this.excelDownloadingSubject.next(isDownloading);
  }

  setExcelDownloaded(isDownloaded: boolean) {
    this.excelDownloadedSubject.next(isDownloaded);
  }

  // NEW: Establish SignalR connection and return it (ready to listen)
  async establishSignalRConnection(): Promise<{ connection: HubConnection; url: string; accessToken: string } | null> {
    try {
      // Step 1: Negotiate with backend
      const negotiateJson = await firstValueFrom(this.reportService.negotiateSignalR());
      if (!negotiateJson || !negotiateJson.data || !negotiateJson.data.url || !negotiateJson.data.accessToken) {
        throw new Error('Invalid negotiate response');
      }

      const url = negotiateJson.data.url;
      const accessToken = negotiateJson.data.accessToken;

      // Step 2: Build SignalR connection
      const connection = new HubConnectionBuilder()
        .withUrl(url, {
          accessTokenFactory: () => accessToken
        })
        .withAutomaticReconnect()
        .build();

      // Step 3: Start connection
      await connection.start();
      console.log(`SignalR connection established and ready`);

      return { connection, url, accessToken };

    } catch (error) {
      console.error(`❌ SignalR connection failed:`, error);
      this.utilityService.showAlert(
        constant.KEY_ERROR,
        constant.ALERT_FAILED_TITLE,
        'Could not establish SignalR connection for Excel export.'
      );
      return null;
    }
  }

  // NEW: Listen for specific jobId on existing connection
  listenForJobReady(jobId: string, connection: HubConnection): void {
    // Sanitize jobId to prevent injection
    const sanitizedJobId = jobId?.replace(/[^a-zA-Z0-9-]/g, '');

    if (!sanitizedJobId || sanitizedJobId !== jobId) {
      console.error('Invalid job ID format:', jobId);
      return;
    }

    if (this.signalRConnections[sanitizedJobId]) {
      return;
    }

    // Store the connection
    this.signalRConnections[sanitizedJobId] = connection;

    // Listen for "JobReady" event
    connection.on("JobReady", (data: any) => {
      if (data && data.jobId === sanitizedJobId) {
        this.lastReadyJobId = sanitizedJobId;

        if (data.fileType === 'pdf') {
          this.pdfReady$.next(sanitizedJobId);
        } else {
          this.excelReady$.next(sanitizedJobId);
        }

        // Remove from pending jobs
        this.removePendingJob(sanitizedJobId);

        // Stop connection
        connection.stop();
        delete this.signalRConnections[sanitizedJobId];
      }
    });

    // NEW: Listen for "JobFailed" event
    connection.on("JobFailed", (data: any) => {
      if (data && data.jobId === sanitizedJobId) {
        console.error(`Job ${sanitizedJobId} failed:`, data.error);

        // Emit failure event
        this.excelJobFailed$.next({
          jobId: sanitizedJobId,
          error: data.error || 'Unknown error occurred'
        });

        // Remove from pending jobs (stops the progress icon)
        this.removePendingJob(sanitizedJobId);

        // Stop connection
        connection.stop();
        delete this.signalRConnections[sanitizedJobId];
      }
    });

    connection.onclose((error) => {
      // Cleanup connection reference
      if (this.signalRConnections[sanitizedJobId]) {
        delete this.signalRConnections[sanitizedJobId];
      }

      if (error) {
        console.error(`SignalR connection closed with error for job ${sanitizedJobId}:`, error);

        // If job is still pending, it means we never got JobReady/JobFailed — treat as failure
        const pendingJobs = this.getPendingJobsFromStorage();
        if (pendingJobs.includes(sanitizedJobId)) {
          this.excelJobFailed$.next({
            jobId: sanitizedJobId,
            error: 'Connection lost before the file was ready. Please try again.'
          });
          this.removePendingJob(sanitizedJobId);
        }
      }
    });
  }

  onExcelDownloaded() {
    this.setExcelDownloaded(true);
    setTimeout(() => {
      this.setExcelDownloaded(false);
    }, 3000);
  }

  onExcelIconClicked() {
    this.setExcelDownloaded(false);
    this.lastReadyJobId = null;
  }

  getLastReadyJobId() {
    return this.lastReadyJobId;
  }

  getPendingJobs(): string[] {
    return this.getPendingJobsFromStorage();
  }

  // UPDATED: Try to resume valid jobs, silently remove expired ones
  resumeAllPendingJobs(): void {
    // Simply refresh the UI to show the pending job icon
    // Don't attempt to download - that will happen when SignalR notifies or user clicks
    this.updatePendingJobsFromStorage();
    this.updateDownloadingState();
  }
}
